import {
    BuildingOffice2Icon,
    CheckCircleIcon,
    QuestionMarkCircleIcon,
    XCircleIcon,
} from "@heroicons/react/20/solid";
import { Fragment, useMemo, useState } from "react";
import {
    disableOTP,
    enableOTP,
    setLoginPassword,
} from "graphql/mutations/auth";
import { showNotification, updateNotification } from "@mantine/notifications";

import { AuthOptions } from "graphql/__generated__/globalTypes";
import { Switch } from "@headlessui/react";
import { classNames } from "utils";
import { logout } from "features/users";
import { useAppSelector } from "../../app/hooks";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { useMutation } from "@apollo/client";
import { usePage } from "hooks/usePage";
import { useTranslation } from "react-i18next";

type PasswordChangeForm = {
    password: string;
    confirm_password: string;
};

type Props = {};

const OTP_TOGGLE_NOTIFICATION_ID = "otp-toggle-status";
const PASSWORD_CHANGE_NOTIFICATION_ID = "password-change-status";

const Profile = (props: Props) => {
    const { t } = useTranslation();
    usePage(`Kava | ${t("profile_page.title")}`);
    const { user } = useAppSelector((state) => state.auth);
    const dispatch = useDispatch();

    const getAuthConfig = () => {
        if (user?.clientable?.__typename === "Reinsurer_representative") {
            return (
                user.clientable.auth_config || {
                    otp_enabled: AuthOptions.No,
                    has_disabled_otp_before: AuthOptions.Yes,
                }
            );
        }
        return {
            otp_enabled: AuthOptions.No,
            has_disabled_otp_before: AuthOptions.Yes,
        };
    };

    const authConfig = getAuthConfig();

    const isOtpInitiallyEnabled = authConfig.otp_enabled === AuthOptions.Yes;
    const hasDisabledOtpBefore =
        authConfig.has_disabled_otp_before === AuthOptions.Yes;

    const [isOtpEnabled, setIsOtpEnabled] = useState(isOtpInitiallyEnabled);

    const [isPasswordChangeRequired, setIsPasswordChangeRequired] =
        useState(false);

    const [enableOtpMutation] = useMutation(enableOTP);
    const [disableOtpMutation] = useMutation(disableOTP);
    const [setLoginPasswordMutation] = useMutation(setLoginPassword);

    const {
        register: registerPassword,
        handleSubmit: handleSubmitPassword,
        formState: { errors: passwordErrors },
        reset: resetPasswordForm,
    } = useForm<PasswordChangeForm>();

    const { register } = useForm<any>({
        defaultValues: {
            first_name:
                user?.clientable?.__typename === "Reinsurer_representative"
                    ? user?.clientable?.rep_first_name
                    : user?.clientable?.assoc_first_name,
            last_name:
                user?.clientable?.__typename === "Reinsurer_representative"
                    ? user?.clientable?.rep_last_name
                    : user?.clientable?.assoc_last_name,
            email:
                user?.clientable?.__typename === "Reinsurer_representative"
                    ? user?.clientable?.rep_email
                    : user?.clientable?.assoc_email,
            primary_phone:
                user?.clientable?.__typename === "Reinsurer_representative"
                    ? user?.clientable?.rep_primary_phonenumber
                    : user?.clientable?.assoc_primary_phonenumber,
            secondary_phone:
                user?.clientable?.__typename === "Reinsurer_representative"
                    ? user?.clientable?.rep_secondary_phonenumber
                    : user?.clientable?.assoc_secondary_phonenumber,
            position: user?.clientable?.position,
        },
    });

    const showPasswordForm = useMemo(() => {
        return isPasswordChangeRequired;
    }, [isPasswordChangeRequired]);

    const performLogout = () => {
        dispatch(logout());
    };

    const handleOtpToggle = async (checked: boolean) => {
        const action = checked ? "enable" : "disable";

        if (isPasswordChangeRequired) {
            showNotification({
                id: OTP_TOGGLE_NOTIFICATION_ID,
                color: "orange",
                title: t("profile_page.notifications.action_blocked_title"),
                message: t("profile_page.notifications.action_blocked_message"),
                icon: <XCircleIcon className="h-5 w-5" />,
            });
            return;
        }

        const isFirstTimeDisabling =
            !checked && isOtpInitiallyEnabled && !hasDisabledOtpBefore;

        if (isFirstTimeDisabling) {
            setIsOtpEnabled(false);
            setIsPasswordChangeRequired(true);
            showNotification({
                id: OTP_TOGGLE_NOTIFICATION_ID,
                color: "yellow",
                title: t("profile_page.notifications.password_required_title"),
                message: t(
                    "profile_page.notifications.password_required_message",
                ),
                icon: <XCircleIcon className="h-5 w-5" />,
            });
            return;
        }
        showNotification({
            id: OTP_TOGGLE_NOTIFICATION_ID,
            loading: true,
            title: `${action.charAt(0).toUpperCase() + action.slice(1)} OTP`,
            message: t("profile_page.notifications.attempting_otp", { action }),
            autoClose: false,
            disallowClose: true,
        });

        const mutation = checked ? enableOtpMutation : disableOtpMutation;
        const mutationName = checked
            ? "enableOTPAuthentication"
            : "disableOTPAuthentication";

        try {
            const response = await mutation({
                variables: {
                    input: {
                        email: user?.email,
                    },
                },
            });

            if (response.data?.[mutationName]?.success) {
                setIsOtpEnabled(checked);
                updateNotification({
                    id: OTP_TOGGLE_NOTIFICATION_ID,
                    color: "green",
                    title: t("profile_page.notifications.success_title"),
                    message: t("profile_page.notifications.otp_success", {
                        action,
                    }),
                    icon: <CheckCircleIcon className="h-5 w-5" />,
                    autoClose: 3000,
                });
                setTimeout(performLogout, 1000);
            } else {
                updateNotification({
                    id: OTP_TOGGLE_NOTIFICATION_ID,
                    color: "red",
                    title: t("profile_page.notifications.otp_failed", {
                        action,
                    }),
                    message:
                        response.data?.[mutationName]?.message ||
                        t("profile_page.notifications.unknown_error"),
                    icon: <XCircleIcon className="h-5 w-5" />,
                    autoClose: 5000,
                });
            }
        } catch (error) {
            updateNotification({
                id: OTP_TOGGLE_NOTIFICATION_ID,
                color: "red",
                title: t("profile_page.notifications.network_error_title"),
                message: t("profile_page.notifications.network_error_otp", {
                    action,
                }),
                icon: <XCircleIcon className="h-5 w-5" />,
                autoClose: 5000,
            });
        }
    };

    const onPasswordChangeSubmit = async (data: PasswordChangeForm) => {
        if (data.password !== data.confirm_password) {
            showNotification({
                id: PASSWORD_CHANGE_NOTIFICATION_ID,
                color: "red",
                title: t("profile_page.notifications.validation_error_title"),
                message: t("profile_page.notifications.passwords_must_match"),
                icon: <XCircleIcon className="h-5 w-5" />,
            });
            return;
        }

        showNotification({
            id: PASSWORD_CHANGE_NOTIFICATION_ID,
            loading: true,
            title: t("profile_page.notifications.processing_title"),
            message: t("profile_page.notifications.processing_message"),
            autoClose: false,
            disallowClose: true,
        });

        const isMandatoryBatch = isPasswordChangeRequired;

        if (isMandatoryBatch) {
            try {
                const disableResponse = await disableOtpMutation({
                    variables: {
                        input: {
                            email: user?.email,
                        },
                    },
                });

                if (!disableResponse.data?.disableOTPAuthentication?.success) {
                    updateNotification({
                        id: PASSWORD_CHANGE_NOTIFICATION_ID,
                        color: "red",
                        title: t(
                            "profile_page.notifications.otp_disable_failed_title",
                        ),
                        message:
                            disableResponse.data?.disableOTPAuthentication
                                ?.message ||
                            t("profile_page.notifications.unknown_error"),
                        icon: <XCircleIcon className="h-5 w-5" />,
                        autoClose: 5000,
                    });
                    return;
                }
            } catch (error) {
                updateNotification({
                    id: PASSWORD_CHANGE_NOTIFICATION_ID,
                    color: "red",
                    title: t("profile_page.notifications.network_error_title"),
                    message: t(
                        "profile_page.notifications.otp_disable_network_error",
                    ),
                    icon: <XCircleIcon className="h-5 w-5" />,
                    autoClose: 5000,
                });
                return;
            }
        }
        try {
            const response = await setLoginPasswordMutation({
                variables: {
                    input: {
                        email: user?.email,
                        password: data.password,
                        confirm_password: data.confirm_password,
                    },
                },
            });

            if (response.data?.setLoginPassword) {
                updateNotification({
                    id: PASSWORD_CHANGE_NOTIFICATION_ID,
                    color: "green",
                    title: t(
                        "profile_page.notifications.security_complete_title",
                    ),
                    message: t(
                        "profile_page.notifications.security_complete_message",
                    ),
                    icon: <CheckCircleIcon className="h-5 w-5" />,
                    autoClose: 3000,
                });

                resetPasswordForm();
                setIsPasswordChangeRequired(false);
                setTimeout(performLogout, 1000);
            } else {
                updateNotification({
                    id: PASSWORD_CHANGE_NOTIFICATION_ID,
                    color: "red",
                    title: t(
                        "profile_page.notifications.password_failed_title",
                    ),
                    message: t(
                        "profile_page.notifications.password_failed_message",
                    ),
                    icon: <XCircleIcon className="h-5 w-5" />,
                    autoClose: 5000,
                });
            }
        } catch (error) {
            updateNotification({
                id: PASSWORD_CHANGE_NOTIFICATION_ID,
                color: "red",
                title: t("profile_page.notifications.network_error_title"),
                message: t("profile_page.notifications.password_network_error"),
                icon: <XCircleIcon className="h-5 w-5" />,
                autoClose: 5000,
            });
        }
    };

    return (
        <Fragment>
            <section className="flex bg-green-50 px-5">
                <div className="h-28 w-28 mr-7">
                    <BuildingOffice2Icon className="text-green-600" />
                </div>
                <div className="w-full flex flex-col">
                    <span className="text-green-800 font-medium text-3xl">
                        {user?.clientable?.__typename ===
                        "Reinsurer_representative"
                            ? user?.clientable?.rep_first_name +
                              " " +
                              user?.clientable?.rep_last_name
                            : user?.clientable?.assoc_first_name +
                              " " +
                              user?.clientable?.assoc_last_name}
                    </span>
                    <div className="flex flex-col">
                        <span className="mr-7 flex flex-row">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-6 w-6 mr-4 text-green-400"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                                />
                            </svg>
                            {user?.email}
                        </span>
                        <span className="flex">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-6 w-6 mr-4 text-green-400"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 019-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
                                />
                            </svg>
                            {"N/A"}
                        </span>
                    </div>
                </div>
            </section>

            <div className="overflow-hidden bg-white shadow sm:rounded-lg mb-6">
                <div className="px-4 py-5 sm:px-6">
                    <h3 className="text-base font-semibold leading-6 text-gray-900">
                        {t("profile_page.company_information")}
                    </h3>
                    <p className="mt-1 max-w-2xl text-sm text-gray-500">
                        {t("profile_page.personal_details")}
                    </p>
                </div>
                <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
                    <dl className="sm:divide-y sm:divide-gray-200">
                        <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5 sm:px-6">
                            <dt className="text-sm font-medium text-gray-500">
                                {t("profile_page.company_name")}
                            </dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                                {user?.clientable?.__typename ===
                                "Reinsurer_representative"
                                    ? user?.clientable?.reinsurer
                                          ?.re_company_name
                                    : ""}
                            </dd>
                        </div>
                        <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5 sm:px-6">
                            <dt className="text-sm font-medium text-gray-500">
                                {t("profile_page.company_email")}
                            </dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                                {user?.clientable?.__typename ===
                                "Reinsurer_representative"
                                    ? user?.clientable?.reinsurer
                                          ?.re_company_email
                                    : ""}
                            </dd>
                        </div>
                        <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5 sm:px-6">
                            <dt className="text-sm font-medium text-gray-500">
                                {t("profile_page.company_website")}
                            </dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                                {user?.clientable?.__typename ===
                                "Reinsurer_representative"
                                    ? user?.clientable?.reinsurer
                                          ?.re_company_website
                                    : ""}
                            </dd>
                        </div>
                    </dl>
                </div>
            </div>

            <section aria-labelledby="profile-details-heading">
                <form action="#" method="POST">
                    <div className="shadow sm:rounded-md sm:overflow-hidden">
                        <div className="bg-white py-6 px-4 sm:p-6">
                            <div>
                                <h2
                                    id="profile-details-heading"
                                    className="text-lg leading-6 font-medium text-gray-900">
                                    {t("profile_page.profile_details")}
                                </h2>
                                <p className="mt-1 text-sm text-gray-500">
                                    {t(
                                        "profile_page.profile_details_description",
                                    )}
                                </p>
                            </div>

                            <div className="mt-6 grid grid-cols-4 gap-6">
                                <div className="col-span-4 sm:col-span-2">
                                    <label
                                        htmlFor="first-name"
                                        className="block text-sm font-medium text-gray-700">
                                        {t("profile_page.first_name")}
                                    </label>
                                    <input
                                        type="text"
                                        {...register("first_name", {
                                            required: "Required",
                                        })}
                                        id="first-name"
                                        readOnly
                                        autoComplete="cc-given-name"
                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"
                                    />
                                </div>

                                <div className="col-span-4 sm:col-span-2">
                                    <label
                                        htmlFor="last-name"
                                        className="block text-sm font-medium text-gray-700">
                                        {t("profile_page.last_name")}
                                    </label>
                                    <input
                                        type="text"
                                        {...register("last_name", {
                                            required: "Required",
                                        })}
                                        id="last-name"
                                        readOnly
                                        autoComplete="cc-family-name"
                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"
                                    />
                                </div>

                                <div className="col-span-4 sm:col-span-2">
                                    <label
                                        htmlFor="email-address"
                                        className="block text-sm font-medium text-gray-700">
                                        {t("profile_page.email_address")}
                                    </label>
                                    <input
                                        type="text"
                                        {...register("email", {
                                            required: "Required",
                                        })}
                                        id="email-address"
                                        readOnly
                                        autoComplete="email"
                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"
                                    />
                                </div>

                                <div className="col-span-4 sm:col-span-2">
                                    <label
                                        htmlFor="expiration-date"
                                        className="block text-sm font-medium text-gray-700">
                                        {t("profile_page.primary_phone")}
                                    </label>
                                    <input
                                        type="text"
                                        {...register("primary_phone", {
                                            required: "Required",
                                        })}
                                        id="expiration-date"
                                        autoComplete="cc-exp"
                                        readOnly
                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"
                                        placeholder=""
                                    />
                                </div>

                                <div className="col-span-4 sm:col-span-2">
                                    <label
                                        htmlFor="security-code"
                                        className="flex items-center text-sm font-medium text-gray-700">
                                        <span>
                                            {t("profile_page.secondary_phone")}
                                        </span>
                                        <QuestionMarkCircleIcon
                                            className="ml-1 flex-shrink-0 h-5 w-5 text-gray-300"
                                            aria-hidden="true"
                                        />
                                    </label>
                                    <input
                                        type="text"
                                        {...register("secondary_phone", {
                                            required: "Required",
                                        })}
                                        id="security-code"
                                        readOnly
                                        autoComplete="cc-csc"
                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"
                                    />
                                </div>

                                <div className="col-span-4 sm:col-span-2">
                                    <label
                                        htmlFor="country"
                                        className="block text-sm font-medium text-gray-700">
                                        {t("profile_page.position")}
                                    </label>
                                    <select
                                        id="country"
                                        {...register("position", {
                                            required: "Required",
                                        })}
                                        autoComplete="country"
                                        className="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                        <option value="Underwriter">
                                            {t("profile_page.underwriter")}
                                        </option>
                                    </select>
                                </div>
                            </div>

                            <div className="mt-8 border-t border-gray-200 pt-6">
                                <h3 className="text-lg leading-6 font-medium text-gray-900">
                                    {t("profile_page.two_factor_auth.title")}
                                </h3>
                                <div className="mt-4 flex justify-between items-center">
                                    <div>
                                        <p className="text-sm font-medium text-gray-700">
                                            {t(
                                                "profile_page.two_factor_auth.otp_authentication",
                                            )}
                                        </p>
                                        <p className="mt-1 text-sm text-gray-500 max-w-xl">
                                            {isOtpEnabled
                                                ? t(
                                                      "profile_page.two_factor_auth.otp_on_description",
                                                  )
                                                : t(
                                                      "profile_page.two_factor_auth.otp_off_description",
                                                  )}
                                        </p>
                                    </div>
                                    <Switch
                                        checked={isOtpEnabled}
                                        onChange={handleOtpToggle}
                                        disabled={isPasswordChangeRequired}
                                        className={classNames(
                                            isOtpEnabled
                                                ? "bg-green-600"
                                                : "bg-gray-200",
                                            "relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2",
                                            isPasswordChangeRequired
                                                ? "opacity-50 cursor-not-allowed"
                                                : "",
                                        )}>
                                        <span className="sr-only">
                                            {t(
                                                "profile_page.two_factor_auth.toggle_otp",
                                            )}
                                        </span>
                                        <span
                                            aria-hidden="true"
                                            className={classNames(
                                                isOtpEnabled
                                                    ? "translate-x-5"
                                                    : "translate-x-0",
                                                "inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out",
                                            )}
                                        />
                                    </Switch>
                                </div>
                                {isPasswordChangeRequired && (
                                    <div className="mt-4 p-4 border-l-4 border-red-500 bg-red-50">
                                        <p className="text-sm font-medium text-red-700">
                                            {t(
                                                "profile_page.two_factor_auth.action_required",
                                            )}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </form>
            </section>

            {showPasswordForm && (
                <section
                    aria-labelledby="password-change-heading"
                    className="mt-6">
                    <form
                        onSubmit={handleSubmitPassword(onPasswordChangeSubmit)}>
                        <div className="shadow sm:rounded-md sm:overflow-hidden">
                            <div className="bg-white py-6 px-4 sm:p-6">
                                <h2
                                    id="password-change-heading"
                                    className="text-lg leading-6 font-medium text-gray-900">
                                    {t("profile_page.password_change.title")}
                                </h2>
                                <p className="mt-1 text-sm text-gray-500">
                                    {t(
                                        "profile_page.password_change.description",
                                    )}
                                </p>

                                <div className="mt-6 grid grid-cols-4 gap-6">
                                    <div className="col-span-4 sm:col-span-2">
                                        <label
                                            htmlFor="new-password"
                                            className="block text-sm font-medium text-gray-700">
                                            {t(
                                                "profile_page.password_change.new_password",
                                            )}
                                        </label>
                                        <input
                                            type="password"
                                            {...registerPassword("password", {
                                                required: t(
                                                    "profile_page.password_change.new_password_required",
                                                ),
                                                minLength: {
                                                    value: 8,
                                                    message: t(
                                                        "profile_page.password_change.min_length",
                                                    ),
                                                },
                                            })}
                                            id="new-password"
                                            autoComplete="new-password"
                                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"
                                        />
                                        {passwordErrors.password && (
                                            <p className="mt-2 text-sm text-red-600">
                                                {
                                                    passwordErrors.password
                                                        .message
                                                }
                                            </p>
                                        )}
                                    </div>

                                    <div className="col-span-4 sm:col-span-2">
                                        <label
                                            htmlFor="confirm-password"
                                            className="block text-sm font-medium text-gray-700">
                                            {t(
                                                "profile_page.password_change.confirm_password",
                                            )}
                                        </label>
                                        <input
                                            type="password"
                                            {...registerPassword(
                                                "confirm_password",
                                                {
                                                    required: t(
                                                        "profile_page.password_change.confirm_password_required",
                                                    ),
                                                },
                                            )}
                                            id="confirm-password"
                                            autoComplete="new-password"
                                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"
                                        />
                                        {passwordErrors.confirm_password && (
                                            <p className="mt-2 text-sm text-red-600">
                                                {
                                                    passwordErrors
                                                        .confirm_password
                                                        .message
                                                }
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
                                <button
                                    type="submit"
                                    className="bg-red-600 border border-transparent rounded-none shadow-sm py-2 px-4 inline-flex justify-center text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-600">
                                    {t(
                                        "profile_page.password_change.save_and_logout",
                                    )}
                                </button>
                            </div>
                        </div>
                    </form>
                </section>
            )}

            <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
                <button
                    disabled
                    type="submit"
                    className="bg-green-800 border border-transparent disabled:bg-gray-400 rounded-none shadow-sm py-2 px-4 inline-flex justify-center text-sm font-medium text-white hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900">
                    {t("profile_page.save")}
                </button>
            </div>
        </Fragment>
    );
};

export default Profile;
